import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useCustomerAuth } from "../AuthContext";
import { Loader2, Mail, Lock } from "lucide-react";
import { toast } from "sonner";
import { auth } from "../damsApi";

export default function SignIn() {
  const { login } = useCustomerAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotBusy, setForgotBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate(from, { replace: true });
    } catch (err) {
      toast.error("Sign-in failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  const onForgot = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Enter your email above first");
      return;
    }
    setForgotBusy(true);
    try {
      await auth.forgot(email);
      toast.success("Check your inbox for the reset code");
    } catch (err) {
      toast.error("Could not send reset email", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setForgotBusy(false);
      setForgotMode(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <div className="card-soft p-8">
        <h1 className="text-2xl font-extrabold tracking-tight mb-1">Welcome back</h1>
        <p className="text-gray-500 mb-6 text-sm">Sign in to manage your reservations.</p>

        <form onSubmit={onSubmit} className="space-y-3" data-testid="signin-form">
          <div className="relative">
            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="email"
              className="input-soft pl-9"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="signin-email"
            />
          </div>
          <div className="relative">
            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="password"
              className="input-soft pl-9"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="signin-password"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            className="pill-btn primary w-full"
            style={{ padding: "0.75rem" }}
            data-testid="signin-submit"
          >
            {busy ? <Loader2 className="animate-spin inline mr-1" size={14} /> : null}
            {busy ? "Signing in..." : "Sign in"}
          </button>
        </form>

        <button
          type="button"
          onClick={onForgot}
          disabled={forgotBusy}
          className="text-xs text-gray-500 hover:text-red-600 mt-3 transition-colors"
          data-testid="signin-forgot"
        >
          {forgotBusy ? "Sending..." : "Forgot password?"}
        </button>

        <p className="text-sm text-gray-600 text-center mt-5">
          Don’t have an account?{" "}
          <Link to="/sign-up" className="text-red-600 font-semibold hover:text-red-700">Sign up</Link>
        </p>
      </div>
    </div>
  );
}
